/**
 * Invoca DNI (dynamic number insertion) helpers.
 *
 * Invoca's number swap is a ONE-SHOT DOM pass fired at tag init (`autoRun`).
 * It does not observe the DOM afterwards, so any phone number React renders -
 * or re-renders - after that pass keeps its untracked base number forever
 * unless the pass is explicitly re-run.
 *
 * `run()` on its own is a no-op once the initial pass has completed: the tag
 * keeps an internal "already ran" guard. `resetRun()` clears that guard, so
 * the two must always be called together.
 *
 * Invoca is not loaded in lower environments, and may be blocked by ad
 * blockers in production, so every call is feature detected and failures are
 * swallowed. A missing tag simply means the untracked base number stays on
 * screen, which is the correct behaviour for those environments - rendering
 * must never be gated on Invoca being present.
 */

const getPnapi = () =>
  (typeof window !== 'undefined' ? window?.Invoca?.PNAPI : null);

export const isInvocaAvailable = () => {
  const pnapi = getPnapi();
  return typeof pnapi?.resetRun === 'function' && typeof pnapi?.run === 'function';
};

/**
 * Whether `subscribeToInvocaComplete` can actually attach a handler.
 *
 * Deliberately NOT `isInvocaAvailable()`: `resetRun`/`run` can exist before
 * `currentPageSettings` does, and subscribing in that window installs nothing
 * while consuming the one-shot subscription.
 */
export const isInvocaSubscribable = () => Boolean(getPnapi()?.currentPageSettings);

/**
 * Re-run Invoca's swap over the current DOM.
 *
 * @returns {boolean} true when the swap was actually triggered, false when
 *          Invoca is unavailable (lower environment, blocked, or not yet
 *          loaded) so callers can decide whether to retry.
 */
export const reswapInvocaNumbers = () => {
  const pnapi = getPnapi();
  if (!isInvocaAvailable()) {
    return false;
  }
  try {
    pnapi.resetRun();
    pnapi.run();
    return true;
  } catch (error) {
    // Third party DOM pass - never allow it to break checkout.
    return false;
  }
};

/**
 * Subscribe to Invoca finishing a swap run.
 *
 * `PNAPI.fireCallbacks` invokes `currentPageSettings.onComplete`, so chaining
 * onto it gives an event driven signal that an assignment is available - no
 * polling. Any existing handler is preserved and restored on unsubscribe.
 *
 * @param {function} onComplete called after each completed Invoca run.
 * @returns {function} unsubscribe.
 */
export const subscribeToInvocaComplete = (onComplete) => {
  const pnapi = getPnapi();
  const settings = pnapi?.currentPageSettings;
  if (!settings) {
    return () => {};
  }
  const previous = settings.onComplete;
  try {
    settings.onComplete = function chainedOnComplete(...args) {
      if (typeof previous === 'function') {
        try {
          previous.apply(this, args);
        } catch (error) {
          // A third party handler must not break ours.
        }
      }
      onComplete();
    };
  } catch (error) {
    // Settings object frozen/sealed by the vendor - nothing to unsubscribe.
    return () => {};
  }
  return () => {
    try {
      settings.onComplete = previous;
    } catch (error) {
      // As above - restoring is best effort.
    }
  };
};

const digits = (value) => String(value || '').replace(/\D/g, '');

/**
 * Read the tracking number Invoca assigned to this session.
 *
 * `PNAPI.placements()` is Invoca's own record of every number it swapped,
 * holding both the original and the assigned number as structured data. It
 * survives anything React does to the DOM afterwards, so this is a reliable
 * pull - unlike observing the swap as a DOM mutation, which is lost the moment
 * a re-render overwrites the node.
 *
 * @param {string} baseTfn the untracked number currently rendered. When
 *        supplied, only placements swapped FROM that number are considered, so
 *        a stale mapping left over from a previous address is ignored.
 * @returns {string} display formatted tracking number, or '' when Invoca is
 *          unavailable or has not assigned one yet.
 */
export const getInvocaTrackingNumber = (baseTfn) => {
  const pnapi = getPnapi();
  if (typeof pnapi?.placements !== 'function') {
    return '';
  }
  const base = digits(baseTfn);
  if (!base) {
    // Without a base there is nothing to match against, and returning the first
    // placement would hand back a number belonging to a different base.
    return '';
  }
  try {
    const placements = pnapi.placements() || [];
    for (let i = 0; i < placements.length; i += 1) {
      const placement = placements[i];
      const tracked = digits(placement?.invocaNumber?.nationalNumber);
      if (!tracked || tracked.length !== 10 || tracked === base) {
        continue;
      }
      if (digits(placement?.phoneNumber?.nationalNumber) !== base) {
        continue;
      }
      return placement.match && digits(placement.match) === tracked
        ? placement.match
        : tracked.replace(/(\d{3})(\d{3})(\d{4})/, '$1-$2-$3');
    }
  } catch (error) {
    return '';
  }
  return '';
};
